/*
 * The applicant pages (Hiring v2, 2026-11-04): what the careers page and the call time page get. Its own file because it
 * uses the client pages' brand shape (billing.zod.ts), which website.zod.ts cannot import without a loop.
 */
import { z } from "zod";
import { publicBrand } from "./billing.zod.js";
import { APPLY_LANGS, BACKGROUND_STATUSES, trialScores } from "./hiring.js";

/** What the careers page shows: the brand and the roles they can pick. */
export const careersPage = z.object({
  brand: publicBrand,
  roles: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      nameEs: z.string(),
      // Phase 3: each open role has its own job page (/apply/<slug>/<role>) with Google Jobs markup.
      about: z.string(),
      aboutEs: z.string(),
      payWords: z.string(),
      payWordsEs: z.string(),
    }),
  ),
  /** Where the work is, like "New Orleans, LA", or null. */
  place: z.string().nullable().default(null),
});
export type CareersPage = z.infer<typeof careersPage>;

/** The page an applicant opens from our text to pick or change their phone screen time (/a/<token>). */
export const applyBooking = z.object({
  brand: careersPage.shape.brand,
  first: z.string(),
  language: z.enum(APPLY_LANGS),
  /** Open times, as instants. */
  slots: z.array(z.string()),
  /** Their booked time, or null. */
  booked: z.string().nullable(),
  minutes: z.number(),
  /** Off the board or hired: nothing to pick. */
  closed: z.boolean(),
  timezone: z.string(),
  /** Phase 2: the trial day, the offer and the paperwork. */
  journey: z.lazy(() => applyJourney),
});
export type ApplyBooking = z.infer<typeof applyBooking>;
export const applyBookInput = z.object({ at: z.iso.datetime() }).strict();

// Phase 2 (owner, 2026-11-04): the same page walks them through the trial day, the offer and the paperwork. -----------

/** Where they are on their page. */
export const APPLY_STEPS = ["call", "trial", "offer", "paperwork", "done", "closed"] as const;
export type ApplyStep = (typeof APPLY_STEPS)[number];

/** What their page shows past the phone call. Lessons come without the right answer. */
export const applyJourney = z.object({
  step: z.enum(APPLY_STEPS),
  trial: z
    .object({ at: z.string(), lead: z.string(), address: z.string(), confirmed: z.boolean(), ratePerHourCents: z.number() })
    .nullable(),
  offer: z.object({ letter: z.string(), signedAt: z.string().nullable(), signedName: z.string().nullable() }).nullable(),
  paperwork: z.object({
    left: z.array(z.string()),
    w9: z.boolean(),
    contact: z.boolean(),
    id: z.boolean(),
    handbookUrl: z.string().nullable(),
    handbook: z.boolean(),
    lessons: z.array(
      z.object({
        id: z.string(),
        title: z.string(),
        body: z.string(),
        question: z.string(),
        choices: z.array(z.string()),
        done: z.boolean(),
      }),
    ),
  }),
  /** Phase 3: the background check link (Checkr's own page), while it waits on them. */
  background: z.object({ url: z.string().nullable(), waiting: z.boolean() }).nullable().default(null),
});
export type ApplyJourney = z.infer<typeof applyJourney>;

export const applySign = z
  .object({
    name: z.string().trim().min(2, "Type your full name.").max(120),
    /** The drawn signature as a data URL, or none. */
    drawing: z.string().max(200_000).nullable().optional(),
  })
  .strict();

export const applyW9 = z
  .object({
    legalName: z.string().trim().min(2, "Type your name as it is on your tax return.").max(120),
    business: z.string().trim().max(120).optional(),
    classification: z.enum(["individual", "llc"]),
    tinType: z.enum(["ssn", "ein"]),
    tin: z
      .string()
      .trim()
      .regex(/^\d{3}-?\d{2}-?\d{4}$|^\d{2}-?\d{7}$/, "Type the 9 numbers."),
    address1: z.string().trim().min(3, "Type your street address.").max(200),
    address2: z.string().trim().max(100).optional(),
    city: z.string().trim().min(2, "Type your city.").max(80),
    state: z.string().trim().length(2, "Type the 2 letter state."),
    zip: z
      .string()
      .trim()
      .regex(/^\d{5}$/, "Type the 5 number ZIP."),
    /** They typed their name to sign under the W-9 certification. */
    signedName: z.string().trim().min(2, "Type your full name to sign.").max(120),
  })
  .strict();
export type ApplyW9 = z.infer<typeof applyW9>;

export const applyContact = z
  .object({
    name: z.string().trim().min(2, "Type a name.").max(120),
    phone: z.string().trim().min(7, "Type a phone number.").max(40),
    relation: z.string().trim().max(60),
    shirt: z.enum(["S", "M", "L", "XL", "2XL", "3XL"]),
  })
  .strict();

export const applyIdPhoto = z
  .object({
    name: z.string().trim().min(1).max(200),
    mime: z.string().regex(/^(image\/[\w.+-]+|application\/pdf)$/, "Send a photo or a PDF."),
    dataBase64: z.string().max(14_000_000, "That file is too big. The most is 10 MB."),
  })
  .strict();

export const applyLesson = z.object({ id: z.string().min(1).max(30), choice: z.number().int().min(0).max(3) }).strict();

/** The lead's scorecard page (/t/<token>). */
export const trialScorePage = z.object({
  brand: publicBrand,
  language: z.enum(APPLY_LANGS),
  first: z.string(),
  scored: z.boolean(),
});
export type TrialScorePage = z.infer<typeof trialScorePage>;
export const trialScoreInput = z.object({ scores: trialScores, note: z.string().trim().max(1000).optional() }).strict();

/** The office books the paid trial day. */
export const trialBookInput = z
  .object({
    at: z.iso.datetime(),
    leadId: z.uuid(),
    /** Where to meet; empty uses the lead's first visit that day. */
    address: z.string().trim().max(300).optional(),
    /** Trial pay per hour; empty uses the role's rate from Hiring settings. */
    rateCents: z.number().int().min(0).max(100_000).optional(),
  })
  .strict();

/** The office writes the offer. */
export const offerInput = z
  .object({
    startOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    payType: z.enum(["percent", "hourly"]),
    percent: z.number().min(0).max(100).nullable(),
    hourlyCents: z.number().int().min(0).max(100_000).nullable(),
  })
  .strict();
export const offerSendInput = offerInput.extend({ letter: z.string().trim().min(20).max(6000) }).strict();

// Phase 3 (owner, 2026-11-04) -------------------------------------------------------------------------------------------

/** The office orders the background check (Checkr emails and we text them the link). Email when the card has none. */
export const backgroundOrderInput = z.object({ email: z.email().max(200).optional() }).strict();
/** The office's call: after a check that needs a look, or one run outside the Steward. */
export const backgroundDecideInput = z
  .object({ status: z.enum(["approved", "declined"]), note: z.string().trim().max(500).optional() })
  .strict();
export const backgroundStatus = z.enum(BACKGROUND_STATUSES);

/** The hiring report: who applied, how far they got, how fast, and who stayed. */
export const hiringReport = z.object({
  from: z.string(),
  to: z.string(),
  /** One row per source (website, careers, Indeed, a referral, typed), most applicants first. */
  sources: z.array(
    z.object({
      source: z.string(),
      applied: z.number(),
      screened: z.number(),
      trials: z.number(),
      offers: z.number(),
      hired: z.number(),
      /** Hired 90 days or more ago and still on the team. */
      stayed90: z.number(),
      /** Hired 90 days or more ago (what stayed90 counts out of). */
      hired90: z.number(),
    }),
  ),
  /** Middle time from applying to our first text, in minutes. Null with none. */
  firstTextMinutes: z.number().nullable(),
  /** Middle days from applying to hired. */
  daysToHire: z.number().nullable(),
  trialsBooked: z.number(),
  trialsShowed: z.number(),
  /** Why people fell out (Not a fit reasons), most first. */
  reasons: z.array(z.object({ reason: z.string(), count: z.number() })),
  /** How many are in each stage now (all time). */
  stages: z.record(z.string(), z.number()),
});
export type HiringReport = z.infer<typeof hiringReport>;
